/*
 * @FilePath: /mall-fe/src/libs/utils.ts
 * @Description: 工具函数集合
 *
 */
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { getEvaParam as getEvaParamApi, getEvaParamSupplier } from 'api/order';
import dayjs from 'dayjs';
import {
  AccountPeriodEnum,
  AgreementPriceMissingReason,
  BillingPeriodEnum,
  CUSTOM_PAGE_TAG,
  EvaluationNodeTypeEnum,
  PORTAL_FURNISH_CONFIG_TYPE_ENUM,
  PORTAL_FURNISH_SUBGROUP,
  PORTAL_FURNISH_TAG_ENUM,
  ShopType,
} from 'libs/enums';
import getConfig from 'next/config';
import { NextRouter } from 'next/router';
import numeral from 'numeral';
import { is } from 'rambdax';
import { Toast } from 'react-vant';
import css from 'styled-jsx/css';
import uuid from 'uuid-random';
import { QIDIAN_SERVICEURL, WOFENG_SERVICE_URL } from './urls';
export {
  buildDownloadUrl,
  getOssPerm,
  getOssProvider,
  getOssUrl,
  isAbsolutePathAliyun,
} from './universal';
export let ossHost = '';
export let obsHost = '';

/**
 * 设置 OBS 主机地址，仅在未设置主机地址时有效
 * @param {string} val - 要设置的 OBS 主机地址
 */
export function setOssHost(val: string) {
  if (!ossHost) {
    ossHost = val;
  }
}

/**
 * 设置 OSS 主机地址，仅在未设置主机地址时有效
 * @param {string} val - 要设置的 OSS 主机地址
 */
export function setObsHost(val: string) {
  if (!obsHost) {
    obsHost = val;
  }
}

/**
 * 根据枚举值获取相应的标签或字段值
 * @param {number} value - 枚举值
 * @param {object} enums - 枚举对象
 * @param {string} field - 要获取的字段名称，默认为 'label'
 * @returns {string} 对应的标签或字段值
 */
export const enumFilter = (value: number, enums: {}, field?: string) => {
  let label = '--';
  Object.keys(enums).forEach((item) => {
    if (enums[item].value === value) {
      label = enums[item][field ?? 'label'];
    }
  });
  return label;
};

export const regExp = {
  phone: /^1\d{10}$/,
  password:
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[%`~!@#$^&*()=|{}':;,\\.<>/?！￥…（）—\\[\]【】‘；：”“。，、？])[A-Za-z\d`%~!@#$^&*()=|{}':;,\\.<>/?！￥…（）—\\[\]【】‘；：”“。，、？]{8,}/,
  email:
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  twoDecimalNum: /^\d+((\.\d{1,2})|\d*)$/,
  chinese: /.*[\u4e00-\u9fa5]+.*$/,
  ChinesePhone:
    /^(0\d{10,11}(-\d{1,4})?)|(0\d{2}-\d{8}(-\d{1,4})?)|(0\d{3}-\d{7,8}(-\d{1,4})?)|(\+86|86)?1[3-9]\d{9}$/i,
  isPhone: /^(\+86|86)?1[3-9]\d{9}$/,
};

/**
 * 计算浏览器滚动条的宽度
 * @returns {number} 滚动条宽度
 */
export function scrollbarWidth() {
  return window.innerWidth - document.body.offsetWidth;
}

// use context instead
// export function buildDownloadUrl(fileUrl: string, fileName: string) {
//   const dispositionHeaderValue = `attachment; filename* = UTF-8''${encodeURI(
//     fileName,
//   )}`
//   return `${fileUrl}?response-content-disposition=${encodeURIComponent(
//     dispositionHeaderValue,
//   )}`
// }

/**
 * 截取字符串并添加省略号
 * @param {string} str - 要截取的字符串
 * @param {number} len - 截取长度，默认为 50
 * @returns {string} 截取后的字符串，若未超过长度则返回原字符串
 */
export function strEllipsis(str: string, len = 50) {
  if (str && str.length > len) {
    return `${str.substring(0, len)}...`;
  }
  return str;
}

export const emptyStyles = css``;

/**
 * 构建完整的 URL
 * @param {string} origin - 原始的 URL 地址
 * @param {string} relativePath - 相对路径
 * @returns {string | undefined} 构建的完整 URL，如果参数不合法则返回 undefined
 */
export function buildUrl(origin: string, relativePath: string) {
  if (!relativePath) return;
  if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
    return relativePath;
  }
  if (!origin) return;
  if (relativePath.startsWith('/')) {
    relativePath = relativePath.slice(1);
  }
  if (ossHost && origin.startsWith(ossHost) && relativePath.startsWith('public-')) {
    origin = obsHost;
  }
  if (origin.endsWith('/')) {
    origin = origin.slice(0, origin.length - 1);
  }
  return `${origin}/${relativePath}`;
}

/**
 * 安全返回URL，确保返回的URL在当前域名内或者是合法的URL
 * @param {string} urlString - 待处理的URL字符串
 * @returns {string} 处理后的URL，如果不符合安全条件，则返回 '/'
 */
export function safeReturnUrl(urlString: string) {
  const { location } = window;
  const localHostArr = location.hostname.split('.');
  let urlHostArr = [];
  try {
    // 以http开头，但是不是http://或者https://的说明就是没有被decode，就应该走进去
    if (/^http(?!s?:\/\/)/.test(urlString)) {
      urlString = decodeURIComponent(urlString);
    }
    urlHostArr = new URL(urlString).hostname.split('.');
  } catch (_e) {
    // ignore
  }
  const localRootHost =
    localHostArr.length > 2 ? localHostArr.slice(1).join('.') : localHostArr.join('.');
  const urlRootHost = urlHostArr.length > 2 ? urlHostArr.slice(1).join('.') : urlHostArr.join('.');
  if (
    urlString.startsWith('/') ||
    urlString.startsWith(location.origin) ||
    localRootHost === urlRootHost ||
    // TODO: temporary 3rd-party setting
    urlRootHost === 'x-jzcg.com' ||
    urlRootHost === 'x-jzcg.cn'
  )
    return urlString;
  return '/';
}

/**
 * 隐藏字符串内容，将部分字符替换为指定字符
 * @param {string} str - 待处理的字符串
 * @param {number} limit - 限制字符串的总长度
 * @param {number} startLen - 保留起始部分长度
 * @param {number} endLen - 保留末尾部分长度
 * @param {string} char - 替换字符，默认为 '*'
 * @param {boolean} fill - 是否填充替换部分，默认为 false
 * @returns {string} 处理后的字符串
 * @throws {RangeError} 如果 startLen 或 endLen 为负数，或者 startLen + endLen 大于 limit
 */
export function hideStrContent(
  str = '',
  limit = 10,
  startLen = 1,
  endLen = 1,
  char = '*',
  fill = false,
) {
  if (startLen < 0 || endLen < 0) throw new RangeError('startLen or endLen is negative');

  if (startLen + endLen > limit) throw new RangeError('startLen + endLen > limit');

  str = str.trim();
  if (str.length <= limit)
    return !fill
      ? str
      : `${str.substring(0, startLen)}${char.repeat(limit - startLen - endLen)}${str.substring(
          str.length - endLen,
        )}`;

  const head = str.substring(0, startLen);
  const end = str.substring(str.length - endLen);
  const repeated = char.repeat(limit - startLen - endLen);

  return `${head}${repeated}${end}`;
}

/**
 * 将对象转换为查询字符串
 * @param {object} obj - 要转换的对象
 * @returns {string} 转换后的查询字符串，如果对象为 null 或没有有效键值对，则返回空字符串
 */
export function toQueryString(obj: {}, isEncode = true) {
  if (obj == null) return '';

  const pairs = Object.entries(obj)
    .filter((v) => v[1])
    .map(
      ([k, v]) =>
        `${isEncode ? encodeURIComponent(k) : k}=${isEncode ? encodeURIComponent(v as string) : v}`,
    );

  if (pairs.length === 0) return '';

  return `?${pairs.join('&')}`;
}

/**
 * 固定格式字符串 转 对象
 * @param str 'a=1; b=2; c=3; ...' => {a:1, b:2, c: 3, ...}
 * @param split ';'
 */
export function strToObj(str: string, split = ';') {
  if (!str) return {};
  const strSplit = str.split(split);
  return strSplit.length > 0
    ? strSplit.reduce((acc, cur) => {
        const curVal = cur.split('=');
        acc[curVal[0].trim()] = curVal[1];
        return acc;
      }, {})
    : {};
}

/**
 * [1, 2] === [1, 2]
 * @param a
 * @param b
 */
export function arrayEquals(a: number[], b: number[]) {
  if (!a || !b) return false;
  if (a === b) return true;
  if (a.length != b.length) return false;

  a = a.sort();
  b = b.sort();
  for (let i = 0; i < a.length; ++i) {
    if (a[i] !== b[i]) return false;
  }

  return true;
}

export interface PromiseResult<T = any> {
  ok: boolean;
  data?: T;
  err?: any;
}

/**
 * 并行处理多个 Promise，返回一个包含每个 Promise 结果的数组，结果带有状态标识
 * @param {Promise<T>[]} promises - 要处理的 Promise 数组
 * @returns {Promise<PromiseResult<T>[]>} 包含每个 Promise 结果的数组
 */
export function allSettled<T = any>(promises: Promise<T>[]) {
  return Promise.all<PromiseResult<T>>(
    promises.map((promise) =>
      promise.then((data) => ({ ok: true, data })).catch((err) => ({ ok: false, err })),
    ),
  );
}

/**
 * 重写重定向 URI，添加授权码参数
 * @param {string} redirectUri - 重定向 URI
 * @param {string} authCode - 授权码
 * @returns {string} 重写后的 URI
 */
export function rewriteUri(redirectUri, authCode) {
  const urlObj = new URL(redirectUri);
  if (urlObj.hash.length > 0) {
    return `${urlObj.origin}${urlObj.pathname}${
      urlObj.search.length > 0 ? '&' : '?'
    }authCode=${authCode}${urlObj.hash}`;
  }
  return `${redirectUri}${urlObj.search.length > 0 ? '&' : '?'}authCode=${authCode}`;
}

/**
 * 将输入值转换为字符串，如果输入值为 null 或 undefined，则返回空字符串
 * @param {any} s - 要转换的值
 * @returns {string} 转换后的字符串
 */
export function ensureStr(s: any) {
  if (s == null) return '';
  return `${s}`;
}

/**
 * 判断当前浏览器类型
 * @returns {string | undefined} 浏览器类型的字符串，如果无法判断则返回 undefined
 */
export function browser() {
  const UA = window.navigator.userAgent;
  if (UA.includes('qqbrowser')) return 'qqbrowser';
  if (UA.includes('baidu')) return 'baidu';
  if (UA.includes('Opera')) return 'Opera';
  if (UA.includes('Edge')) return 'Edge';
  if (UA.includes('MSIE') || (UA.includes('Trident') && UA.includes('rv:11.0'))) return 'IE';
  if (UA.includes('Firefox')) return 'Firefox';
  if (UA.includes('Chrome')) return 'Chrome';
  if (UA.includes('Safari')) return 'Safari';
}

/**
 * Transform an object keys using the given fn.
 */
export function mapKeys(obj: { [k: string]: any }, fn: (k: string, v: any) => string) {
  return Object.entries(obj).reduce((o, [k, v]) => {
    o[fn(k, v)] = v;
    return o;
  }, {});
}

/**
 * Transform an object values using the given fn.
 */
export function mapValues(obj: { [k: string]: any }, fn: (k: string, v: any) => any) {
  return Object.entries(obj).reduce((o, [k, v]) => {
    o[k] = fn(k, v);
    return o;
  }, {});
}

// inspect and return value
export function io(v: any) {
  console.log(v);
  return v;
}

/**
 * 生成带前缀的唯一元素ID
 * @param {string} prefix - ID的前缀
 * @returns {string} 生成的唯一元素ID
 */
export function generateElementID(prefix: string) {
  return `${prefix}_${Date.now()}_${~~(Math.random() * 1000)}`;
}

// eslint-disable-next-line
export const noop = (..._a: any) => {};

/**
 * 解析查询字符串为对象
 * @param {string} queryString - 要解析的查询字符串
 * @returns {object} 解析后的对象，键为查询参数名，值为查询参数值
 */
export function parseQuery(queryString) {
  const query = {};
  const pairs = (queryString[0] === '?' ? queryString.substr(1) : queryString).split('&');
  for (let i = 0; i < pairs.length; i++) {
    const pair = pairs[i].split('=');
    query[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || '');
  }
  return query;
}

// 金额转大写 from ifs-eui
//
export const formatChineseNumeral = (value) => {
  if (value === null || value === undefined || value === '') {
    return;
  }
  // eslint-disable-next-line
  if (!/^\-{0,1}(0|[1-9]\d*)(\.\d+)?$/.test(value)) {
    return;
  }
  if (Number(value) === 0) {
    return '零';
  }
  let sign = '';
  if (Number(value) < 0) {
    sign = '负';
  }
  let unit = '千百拾亿千百拾万千百拾元角分';
  let str = '';
  value += '00';
  const p = value.indexOf('.');
  if (p >= 0) {
    value = value.substring(0, p) + value.substr(p + 1, 2);
  }
  value = Math.abs(value).toString();
  unit = unit.substr(unit.length - value.length);
  for (let i = 0; i < value.length; i++) {
    str += '零壹贰叁肆伍陆柒捌玖'.charAt(value.charAt(i)) + unit.charAt(i);
  }

  return (
    sign +
    str
      .replace(/零(千|百|拾|角)/g, '零')
      .replace(/(零)+/g, '零')
      .replace(/零(万|亿|元)/g, '$1')
      .replace(/(亿)万|壹(拾)/g, '$1$2')
      .replace(/^元零?|零分/g, '')
      .replace(/元$/g, '元整')
  );
};

// 用于服务端渲染场景判断是否在 tabe app 中
export function judgeTabeapp(header) {
  const userAgent = header['user-agent'] ?? '';
  const isTabeApp = userAgent.includes('tabeapp');
  return {
    isTabeApp,
    isAndroidTabeApp: isTabeApp && userAgent.includes('android'),
    isIosTabeApp: isTabeApp && userAgent.includes('ios'),
  };
}

/**
 * 根据URL获取其主机和路径部分
 * @param {string} url - 要解析的URL
 * @returns {string} 主机和路径组成的字符串
 */
export function getHrefByUrl(url: string) {
  if (!url) return;

  try {
    const reg = /(http|https):\/\/S*/;
    if (!reg.test(url)) return url;

    const path = new URL(url);
    return path.host + path.pathname;
  } catch (err) {
    return '';
  }
}

/**
 * 将日期格式化为指定格式的字符串
 * @param {any} date - 要格式化的日期
 * @param {string} fmt - 格式化字符串的格式，默认为'YYYY-MM-DD'
 * @returns {string} 格式化后的日期字符串
 */
export const dateFormat = (date: any, fmt = 'YYYY-MM-DD') => {
  if (!date) return '--';
  return dayjs(date).format(fmt);
};

/**
 * 格式化时间距离当前时间的描述字符串
 * @param {number} inDate - 距离当前时间的秒数
 * @param {boolean} mobile - 是否为移动设备
 * @returns {string} 格式化后的时间描述字符串
 */
export function formatBeforeDate(inDate, mobile = false) {
  const time = (new Date().getTime() - inDate) / 1000;
  //存储转换值
  let s;
  if (time < 60 * 10) {
    //十分钟内
    return '刚刚';
  } else if (time < 60 * 60 && time >= 60 * 10) {
    //超过十分钟少于1小时
    s = Math.floor(time / 60);
    return s + '分钟前';
  } else if (time < 60 * 60 * 24 && time >= 60 * 60) {
    //超过1小时少于24小时
    s = Math.floor(time / 60 / 60);
    return s + '小时前';
  } else if ((time < 60 * 60 * 24 * 3 && time >= 60 * 60 * 24) || mobile) {
    //超过1天少于3天内
    s = Math.floor(time / 60 / 60 / 24);
    return s + '天前';
  } else {
    //超过3天
    // return dateFormat(inDate, "YYYY-MM-DD HH:mm:ss")
    return dateFormat(inDate, 'YYYY-MM-DD');
  }
}

/**
 * 获取枚举值对应的标签
 * @param {object} enums - 枚举对象
 * @param {string | number} value - 要查找的枚举值
 * @returns {string | undefined} 枚举值对应的标签，如果找不到返回 undefined
 */
export function getEnumLabel({ enums, value }: { enums: Enum; value: string | number }) {
  let label;
  for (let key in enums) {
    const item = enums[key];
    if (item.value === value) {
      label = item.label;
      break;
    }
  }
  return label;
}

/**
 * 生成一个从0到n-1的整数数组
 * @param {number} n - 数组长度
 * @returns {number[]} 生成的整数数组
 */
export function range(n: number) {
  const arr = new Array(n);
  for (let i = 0; i < n; i++) {
    arr[i] = i;
  }
  return arr;
}

/**
 * 异步获取当前位置的坐标信息
 * @param {object} options - 位置获取的选项
 * @returns {Promise<any>} Promise，包含位置的坐标信息
 * navigator.geolocation.getCurrentPosition(successCallback, errorCallback, options);
 */
export async function getCurrentPosition(options?: any): Promise<any> {
  const geolocationOptions = {
    enableHighAccuracy: true, // 高精度
    timeout: 5 * 1000, // API调用超时时间单位 ms 5s
    maximumAge: 0, // 缓存地理位置时间ms 1h
  };

  return new Promise((resolve, reject) => {
    setTimeout(() => {
      reject('no-operation');
    }, 5000);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        /**
         * pos.coords.latitude  经度
         * pos.coords.longitude 纬度
         */
        resolve(pos);
      },
      (e) => {
        console.error(e);
        reject(e);
      },
      {
        ...geolocationOptions,
        ...options,
      },
    );
  });
}

/**
 * 判断对象是否为 Window 对象
 * @param {any} obj - 要判断的对象
 * @returns {boolean} 如果对象是 Window 对象返回 true，否则返回 false
 */
export function isWindow(obj: any) {
  return obj !== null && obj !== undefined && obj === obj.window;
}

/**
 * 获取滚动位置
 * @param {HTMLElement | Window | Document | null} target - 滚动的目标元素
 * @param {boolean} top - 是否获取垂直滚动位置
 * @returns {number} 滚动位置的数值
 */
export function getScroll(target: HTMLElement | Window | Document | null, top: boolean): number {
  if (typeof window === 'undefined') {
    return 0;
  }
  const method = top ? 'scrollTop' : 'scrollLeft';
  let result = 0;
  if (isWindow(target)) {
    result = (target as Window)[top ? 'pageYOffset' : 'pageXOffset'];
  } else if (target instanceof Document) {
    result = target.documentElement[method];
  } else if (target) {
    result = (target as HTMLElement)[method];
  }
  if (target && !isWindow(target) && typeof result !== 'number') {
    result = ((target as HTMLElement).ownerDocument || (target as Document)).documentElement?.[
      method
    ];
  }
  return result;
}

/**
 * 判断是否为移动商城
 * @param {any} req - 请求对象
 * @returns {boolean} 如果是移动商城返回 true，否则返回 false
 */
export function isMobileMall(req) {
  if (req) {
    const subdomain = req?.hostname?.split('.')[0] ?? '';
    return !!(
      process.env.M || //NOTE: 开发阶段-使用M运行移动端
      subdomain.toLowerCase() === 'm' || // NOTE: 线上运行时-通过域名来判断是否是「移动端」
      subdomain.includes('m-') ||
      subdomain.endsWith('-m')
    );
  } else {
    const subdomain = location.hostname.split('.')[0] ?? '';
    return !!(
      process.env.M ||
      subdomain.toLowerCase() === 'm' ||
      subdomain.includes('m-') ||
      subdomain.endsWith('-m')
    );
  }
}

/**
 * 判断当前sku不可售
 * @param skuInfo
 * @returns
 */
export function isDisabledShopSku(skuInfo: SkuInfo, tradeAgreementList: TradeAgreementInfo[] = []) {
  if (
    skuInfo.mallSku.status === 0 ||
    !skuInfo.mallSku.warehouseSysNo ||
    skuInfo.hasOwnSku ||
    !skuInfo.mallSku.hasQuotedPrice ||
    !skuInfo.mallSku.canSale ||
    (!!tradeAgreementList.length && skuInfo.agreementCanSale === false) ||
    skuInfo.inventory === 0 ||
    (!skuInfo.noAgreementCanSale && !tradeAgreementList?.length) ||
    noSaleByBasePrice(skuInfo)
  ) {
    return true;
  }
  return false;
}

/**
 * 根据基础价格判断是否不可销售
 * @param {SkuInfo} skuInfo - SKU信息对象
 * @returns {boolean} 如果根据基础价格判断不可销售返回 true，否则返回 false
 */
export function noSaleByBasePrice(skuInfo: SkuInfo) {
  return (
    skuInfo.mallSku?.salesPrice === null &&
    skuInfo.agreementCanSale &&
    +skuInfo.skuForPriceCalculate?.agreementPriceMissingReason ===
      AgreementPriceMissingReason.TRADE_AGREEMENT_PRICE_MISSING
  );
}

/**
 * 判断当前order sku不可售
 * @param skuInfo
 * @returns
 */
export function isDisabledOrderSku(skuInfo: OrderSkuInfo, tradeAgreementSysNo: string) {
  if (
    skuInfo.status === 0 ||
    !skuInfo.warehouseSysNo ||
    skuInfo.hasOwnSku ||
    !skuInfo.hasQuotedPrice ||
    !skuInfo.canSale ||
    (tradeAgreementSysNo && skuInfo.agreementCanSale === false) ||
    skuInfo.inventory === 0
  ) {
    return true;
  }
  return false;
}

/**
 * 判断是否存在SKU错误信息
 * @param {OrderSkuInfo} skuInfo - SKU信息对象
 * @param {string} tradeAgreementSysNo - 交易协议系统编号
 * @returns {boolean} 如果存在SKU错误信息返回 true，否则返回 false
 */
export function hasSkuErrorInfo(skuInfo: OrderSkuInfo, tradeAgreementSysNo: string) {
  if (
    noSaleByBasePrice2(skuInfo) || // 没有协议基价
    skuInfo.status === 0 || // 商品已下架
    !skuInfo.warehouseSysNo || // 此区域不可供
    skuInfo.hasOwnSku || // 请勿购买自己的商品
    !skuInfo.hasQuotedPrice || // 无库存
    !skuInfo.canSale || // 不可售
    Number(skuInfo.shopQuantity) < skuInfo.buyStart || // 不满足订购数量要求
    !skuInfo.shopQuantity || // 无商品数量
    skuInfo.salesPrice <= 0 || // 无销售价格
    (tradeAgreementSysNo && // 暂不支持依据此协议下单
      skuInfo.agreementCanSale === false &&
      skuInfo.sourceType === 3 &&
      !skuInfo.salesPrice &&
      skuInfo.canSale) ||
    (tradeAgreementSysNo && skuInfo.agreementCanSale === false) // 该商品与协议未绑定
  ) {
    return true;
  }
  return false;
}

/**
 * 两个地方数据结构不一致，且在下单页，salePrice可以自己填
 */
export function noSaleByBasePrice2(skuInfo) {
  return (
    skuInfo.agreementCanSale &&
    +skuInfo.skuForPriceCalculate?.agreementPriceMissingReason ===
      AgreementPriceMissingReason.TRADE_AGREEMENT_PRICE_MISSING
  );
}

/**
 * 根据当前页面的 location 生成对应的域名
 * @param {Location} location - 当前页面的 location 对象
 * @returns {string} 生成的域名字符串
 */
export function genDomain(location) {
  const host = location.hostname;
  const hostSplit = host.split('.');
  let domainStr = '';
  if (hostSplit.length >= 3) {
    hostSplit.shift();
    domainStr = `.${hostSplit.join('.')}`;
  } else {
    domainStr = host.indexOf('.') > 0 ? `.${host}` : host;
  }
  return domainStr;
}

/**
 * 设置 Cookie
 * @param {string} key - Cookie 键名
 * @param {string} value - Cookie 值
 * @param {Location} location - 当前页面的 location 对象
 */
export function setCookie(key: string, value: string, location: Location) {
  document.cookie = `${key}=${value}; domain=${genDomain(location)}; path=/; Secure; SameSite=None`;
}

/**
 * 获取本地存储中的值
 * @param {string} key - 存储键名
 * @returns {object|null} 从本地存储中获取的对象，如果不存在则返回 null
 */
export function getLocalStorage(key: string) {
  let result = null;
  try {
    result = JSON.parse(localStorage.getItem(key));
  } catch (err) {
    console.error(err);
  }
  return result;
}

/**
 * 将值存储到本地存储中
 * @param {string} key - 存储键名
 * @param {object} value - 存储的对象
 */
export function setLocalStorage(key: string, value: object) {
  localStorage.setItem(key, JSON.stringify(value));
}

/**
 * 在数字前补零，使其达到指定长度
 * @param {any} val - 待补零的数字
 * @param {number} length - 目标长度
 * @returns {string} 补零后的结果字符串
 */
export function padZero(val: any, length: number) {
  var len = val.toString().length;
  if (len >= length) {
    return val;
  } else {
    while (len < length) {
      val = '0' + val;
      len++;
    }
    return val;
  }
}

/**
 * 复制文本内容到剪贴板
 * @param {string} text - 待复制的文本内容
 */
export async function copyContent(text: string) {
  const txa: any = document.createElement('textarea');
  txa.value = text;
  txa.setAttribute('readonly', '');
  txa.style.position = 'absolute';
  txa.style.left = '-9999px';
  document.body.appendChild(txa);
  txa.select();
  document.execCommand('copy');
  document.body.removeChild(txa);
  Toast.success('复制成功！');
}

/**
 * 格式化渲染价格，添加千位分隔符和保留两位小数
 * @param {number} val - 待格式化的价格
 * @returns {string} 格式化后的价格字符串，如果不存在则返回 '--'
 */
export function formatRenderPrice(val: number) {
  return val ? numeral(val).format('0,0.00[000000]') : '--';
}

/**
 * 格式化渲染总价格，添加千位分隔符和保留两位小数
 * @param {number} val - 待格式化的总价格
 * @returns {string} 格式化后的总价格字符串，如果不存在则返回 '--'
 */
export function formatRenderTotalPrice(val: number) {
  return val ? numeral(val).format('0,0.00') : '--';
}

/**
 * 格式化价格，保留两位小数，并根据位数添加千位分隔符
 * @param {number} val - 待格式化的价格
 * @returns {string} 格式化后的价格字符串，如果不存在则返回 '--'
 */
export function formatPrice(val: number) {
  return val ? numeral(val).format('0.00[000000]') : '--';
}

/**
 * 格式化总价格，保留两位小数
 * @param {number} val - 待格式化的总价格
 * @returns {string} 格式化后的总价格字符串，如果不存在则返回 '--'
 */
export function formatTotalPrice(val: number | string) {
  return val || val === 0 ? numeral(val).format('0.00') : '--';
}

/**
 * 构建账期列表
 * @param {Array} discountByMethodList - 账期信息列表
 * @param {string} supplierSysNo - 供应商系统编号
 * @param {number} billingPeriodOtherDays - 其他账期天数
 * @returns {Array} 构建的账期列表
 */
export function buildPeriodList(
  discountByMethodList,
  supplierSysNo,
  billingPeriodOtherDays?: number,
) {
  const list = discountByMethodList.map((item) => {
    if (item.accountPeriod === AccountPeriodEnum.CUSTOM.value) {
      if (item.billingPeriod == BillingPeriodEnum.OTHER.value && !!billingPeriodOtherDays) {
        item.name = `结算付款（${billingPeriodOtherDays}天后一次性付款）`;
      } else {
        item.name = getEnumLabel({
          enums: BillingPeriodEnum,
          value: item.billingPeriod,
        });
      }
    } else {
      item.name = getEnumLabel({
        enums: AccountPeriodEnum,
        value: item.accountPeriod,
      });
    }
    item.sysNo = item.accountPeriod + '-' + (item.billingPeriod == null ? '' : item.billingPeriod);
    item.supplierSysNo = supplierSysNo;
    return item;
  });
  return list;
}

/**
 * 在枚举中查找指定值的对应标签
 * @param {Record<string, { value: number | string; label: string }>} enam - 枚举对象
 * @param {number | string} val - 待查找的值
 * @returns {string} 找到的标签，如果未找到则返回空字符串
 */
export function findEnum(enam: Record<string, { value: number | string; label: string }>, val) {
  return Object.values(enam).find((e) => e.value === val)?.label ?? '';
}

/**
 * 计算数组中所有元素的和
 * @param {Array} arr - 待计算的数组
 * @returns {number} 数组中所有元素的和
 */
export function getSum(arr) {
  return arr.reduce((x, y) => +x + +y, 0);
}

/**
 * 根据传入值的类型 返回清空后的值
 * @param val
 * @returns {null|Array|boolean}
 */
export function clearDataByValType(val) {
  if (is(Array, val)) {
    return [];
  } else if (is(Boolean, val)) {
    return false;
  } else {
    return null;
  }
}

export function getShopProRedirectObj({
  type,
  shopSysNo,
  preview,
  notFound,
}: {
  type: ShopType;
  shopSysNo: string;
  preview: boolean;
  notFound: boolean;
}) {
  if (notFound) {
    return {
      redirect: {
        permanent: false,
        basePath: false,
        destination: '/pages/shop/pro/404',
      },
    };
  } else if (type === ShopType.PRO || type === undefined || preview) {
    return {};
  } else {
    return {
      redirect: {
        permanent: false,
        basePath: false,
        destination: `/shop/index/${shopSysNo}`,
      },
    };
  }
}

export function getShopRedirectObj({
  type,
  shopSysNo,
  preview,
  notFound,
  isMobile,
}: {
  type: ShopType;
  shopSysNo: string;
  preview: boolean;
  notFound: boolean;
  isMobile: boolean;
}) {
  if (notFound) {
    return {
      redirect: {
        permanent: false,
        basePath: false,
        destination: '/pages/shop/pro/404',
      },
    };
  } else if ((type === ShopType.PRO || type === undefined || preview) && !isMobile) {
    return {
      redirect: {
        permanent: false,
        basePath: false,
        destination: `/pages/shop/pro/index/${shopSysNo}`,
      },
    };
  } else {
    return {};
  }
}

/**
 * 是否部署在阿里云上，取决于打包的时候传的env
 */
export function isOnAliyun(): boolean {
  const { publicRuntimeConfig } = getConfig();
  return ['zjga', 'zjpre', 'zjdev', 'zjst', 'itipre'].includes(publicRuntimeConfig.ENV);
}

export function getFurnishPageRedirectObj({
  tag,
  notFound,
}: {
  tag: CUSTOM_PAGE_TAG;
  notFound: boolean;
}) {
  if (notFound) {
    return {
      redirect: {
        permanent: false,
        basePath: false,
        destination: '/404',
      },
    };
  } else if (tag === CUSTOM_PAGE_TAG.MALL_INDEX) {
    return {
      redirect: {
        permanent: false,
        basePath: false,
        destination: '/',
      },
    };
  } else {
    return {};
  }
}

export function getPageUrl(data, tenantInfo, isView) {
  const prefix = isView ? '' : '//';
  const mallUrl = tenantInfo?.basic?.mallDomain;
  const platformUrl = tenantInfo?.basic?.platformDomain;
  if (data.configType === PORTAL_FURNISH_CONFIG_TYPE_ENUM.LINK.value) {
    return data?.pageSetting?.uiConfig?.[0]?.jsonConfig?.linkUrl;
  } else if (data.subgroup === PORTAL_FURNISH_SUBGROUP.COMMON.value) {
    if (data.tag === PORTAL_FURNISH_TAG_ENUM.LOGIN.value) {
      return `${prefix}${platformUrl}/sso/login`;
    }
  } else if (data.subgroup === PORTAL_FURNISH_SUBGROUP.MALL.value) {
    if (data.tag === PORTAL_FURNISH_TAG_ENUM.MALL_INDEX.value) {
      return prefix + mallUrl + '/';
    } else {
      return `${prefix}${mallUrl}/p/${data.sysNo}`;
    }
  }
}

export function getEvaButtonText(evaParam, nodeType) {
  if (nodeType === EvaluationNodeTypeEnum.ORDER_TRADE_DELIVERY_TRANSPORT.value) {
    if (evaParam?.status === 0) {
      return '物流评价';
    } else {
      return '查看物流评价';
    }
  } else {
    if (evaParam?.status === 0) {
      return '立即评价';
    } else {
      if (evaParam?.isNotEvaluationUser) {
        return '查看对方评价';
      } else {
        return '查看我的评价';
      }
    }
  }
}

/**
 * 原来直接用queryString传值，现在param特别大，只有用sessionStorage暂存一下了
 * @param resData
 */
export function setEvaParam(resData) {
  sessionStorage.setItem('evaParam', JSON.stringify(resData));
}

export function getEvaParam(): Partial<DynamicEvaluation> {
  const str = sessionStorage.getItem('evaParam');
  if (str === null) {
    return {};
  } else {
    const params = JSON.parse(str) as DynamicEvaluation;
    return params;
  }
}

export function goToEva(resData, router: NextRouter) {
  setEvaParam(resData);
  router.push({
    pathname: '/order/evaluate',
    // query: res.data as any,
  });
}

export function getEvaEvents(obj: any, router: NextRouter, role: ApplicationKey): any[] {
  const dl: DynamicEvaluation[] = obj?.dynamicEvaluationList?.filter((d) => d.needEvaluation) ?? [];
  dl.sort((l, r) => {
    // 未评价在已评价之前
    // 我的评价在对我的评价之前
    // 物流在最后
    if (l.status !== l.status) {
      // 未评价：STATUS = 0 排在 已评价：STATUS = 1 前头
      return l.status - r.status;
    } else {
      if (l.nodeType === EvaluationNodeTypeEnum.ORDER_TRADE_DELIVERY_TRANSPORT.value) {
        return 1;
      } else if (r.nodeType === EvaluationNodeTypeEnum.ORDER_TRADE_DELIVERY_TRANSPORT.value) {
        return -1;
      } else {
        const lPositive = l.isNotEvaluationUser ? 1 : 0;
        const rPositive = r.isNotEvaluationUser ? 1 : 0;
        return lPositive - rPositive;
      }
    }
  });
  // console.log("dl", dl)
  return dl.map((d) => {
    return {
      cls: '',
      text: getEvaButtonText(d, d.nodeType),
      handle() {
        const req =
          role === 'supplier'
            ? getEvaParamSupplier({
                json: d,
              })
            : getEvaParamApi({ json: d });

        req.then((res) => {
          if (res.code === 200) {
            goToEva(res.data, router);
          }
        });
      },
    };
  });
}

/**
 * 获取uuid唯一值
 */
export async function getUuidToken() {
  const fpPromise = FingerprintJS.load();
  const fp = await fpPromise;
  const result = await fp.get();
  const { visitorId } = result;
  const reg = new RegExp('-', 'g'); // 加'g'，删除字符串里所有的"a"
  return visitorId.replace(reg, '');
}
/**
 * 多页面调用企点客服接入的参数，写成公用函数。
 * @param data 用户信息
 * @param {boolean} isSelfSupport 是否云采自营
 * @returns
 */
export async function getQiDianServiceParams(data?: any, isDelivery?: boolean) {
  const { publicRuntimeConfig } = getConfig();
  const { SPECIAL_PRO_SHOP, GENERAL_URL, LOGISTICS_EXPRESS_URL } = QIDIAN_SERVICEURL;
  const roleName = {
    supplier: '供应商',
    purchaser: '采购商',
    operation: '运营商',
    'supply-chain': '赊销系统',
  };

  let uuid_token = localStorage.getItem(`UUIDTOKEN`);
  if (!uuid_token) {
    // 生成临时身份，本地存储一次
    uuid_token = await getUuidToken();
    localStorage.setItem(`UUIDTOKEN`, uuid_token);
  }
  /**
   * 当前是云采高级店铺时，使用云采对应的客服url
   * 其他页面使用通用客服url
   * 增加快递专用客服url
   */
  const url = isDelivery
    ? LOGISTICS_EXPRESS_URL
    : ['zjga', 'zjpre'].includes(publicRuntimeConfig.ENV || 'zjdev')
    ? GENERAL_URL
    : SPECIAL_PRO_SHOP;
  if (!data || !data?.profile) {
    return {
      url,
      code: uuid_token,
      visitorFlag: false,
      supplierSysNo: data?.supplierSysNo || null,
    };
  }
  const tradeType = (data?.applications?.map((item) => roleName[item?.key]) || []).join('/');

  return {
    name: data?.profile?.displayName || '',
    code: data?.profile?.sysNo || '',
    organizationName: data?.profile?.organizationName || '',
    tradeType,
    url,
    visitorFlag: true,
    supplierSysNo: data?.supplierSysNo || null,
  };
}

// PC 端传入为空，由后台跟进 supplyID 选择对应的客服URL，无则返回平台
export async function getWofengServiceParams(data?: any) {
  const url = isMobileMall() ? WOFENG_SERVICE_URL.YXSC_MOBILE : '';

  if (!data || !data?.profile) {
    return {
      code: '', // 为空表示访客模式
      supplierSysNo: data?.supplierSysNo || '',
      url,
    };
  }
  const roleName = {
    supplier: '供应商',
    purchaser: '采购商',
    operation: '运营商',
    'supply-chain': '赊销系统',
  };

  // const uuid_token = await getUuidToken();
  /**
   * 当前是云采高级店铺时，使用云采对应的客服url
   * 其他页面使用通用客服url
   * 增加快递专用客服url
   */

  const myApp = data?.applications ?? [];
  const tradeType = (myApp?.map((item) => roleName[item?.key]) || []).filter(Boolean).join('/');

  return {
    name: data?.profile?.displayName || '',
    organizationName: data?.profile?.organizationName || '',
    code: data?.profile?.sysNo || '',
    tradeType,
    supplierSysNo: data?.supplierSysNo || '',
    url,
    vipFlag: myApp[0]?.key === 'supplier' ? true : false, // 供应商专职客服标识
    // sessionKey: uuid_token,
  };
}

export const isArray = <T = any>(array): array is Array<T> =>
  Array.isArray(array) && !!array.length;

export const isNull = (val) => val === null || val === '' || val === undefined;

export const jsonParse = (str: string) => {
  if (str === null || str === undefined || typeof str !== 'string') return str;
  try {
    return JSON.parse(str);
  } catch {
    return str;
  }
};

/**
 * 数组遍历添加唯一key值
 * @param array
 * @returns array
 */
export const generateUniqueKey = <T = Record<string, any>>(
  array: Array<T>,
): Array<T & { __key: string }> => {
  return array.map((item) => ({
    ...item,
    __key: uuid(),
  }));
};

/**
 * 十六进制颜色值的正则表达式
 * @param colorStr
 * @returns rgb
 */
export const colorRgb = (colorStr: string, opacity = 1) => {
  const reg = /^#([0-9a-fA-f]{3}|[0-9a-fA-f]{6})$/;
  let sColor = colorStr.toLowerCase();
  if (sColor && reg.test(sColor)) {
    if (sColor.length === 4) {
      let sColorNew = '#';
      for (let i = 1; i < 4; i += 1) {
        sColorNew += sColor.slice(i, i + 1).concat(sColor.slice(i, i + 1));
      }
      sColor = sColorNew;
    }
    //处理六位的颜色值f
    const sColorChange = [];
    for (var i = 1; i < 7; i += 2) {
      sColorChange.push(parseInt('0x' + sColor.slice(i, i + 2)));
    }
    return `rgb(${sColorChange.join(',')},${opacity})`;
  } else {
    return sColor;
  }
};

export const splitNum = (num?: number | string | null) => {
  const tuple = (num != null && numeral(+num).format('0.00[000000]').split('.')) || [];
  return tuple;
};

export function OtherText(val, modelKey = 'model') {
  let text = '';
  // if (val.otherAttribute?.length) {
  //   for (const a of val.otherAttribute) {
  //     if (a.attributeValueList?.length) {
  //       text += `${a.attributeName}: ${a.attributeValueList
  //         .map((v) => v.attributeValueName)
  //         .join('、')}; `;
  //     }
  //   }
  // } else {
  text = val[modelKey];
  // }
  return text;
}

// 是不是在商链通App中
export function isInSltApp(userAgent) {
  return userAgent?.includes('cctbsupplier/1');
}

/**
 * 获取枚举值对应的标签
 * @param {object} enums - 枚举对象
 * @param {string | number} value - 要查找的枚举值
 * @returns {string | undefined} 枚举值对应的标签，如果找不到返回 undefined
 */
export function getEnumKey({
  enums,
  value,
  keys = 'label',
}: {
  enums: Enum;
  value: string | number;
  keys?: string;
}) {
  let label;
  for (let key in enums) {
    const item = enums[key];
    if (item.value === value) {
      label = item[keys];
      break;
    }
  }
  return label;
}

export const formatMilliseconds = (ms: number) => {
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((ms % (1000 * 60)) / 1000);

  let result = '';
  if (hours > 0) {
    result += `${hours}时`;
  }
  if (minutes > 0) {
    result += `${minutes}分`;
  }
  if (seconds > 0) {
    result += `${seconds}秒`;
  }
  return result || '--';
};
